import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type NostrSigner, NostrEvent, NostrFilter, NPool, NRelay1 } from '@nostrify/nostrify';
import { NostrContext } from '@nostrify/react';
import { NUser, useNostrLogin } from '@nostrify/react/login';
import { useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/hooks/useAppContext';
import { APP_RELAYS } from '@/lib/appRelays';

/** Derive a stable fingerprint for a relay config so we only invalidate on real changes. */
function relayFingerprint(relays: { url: string; read: boolean; write: boolean }[]): string {
  return relays
    .map((r) => `${r.url}:${r.read ? 'r' : ''}${r.write ? 'w' : ''}`)
    .sort()
    .join('|');
}

interface NostrProviderProps {
  children: React.ReactNode;
}

const NostrProvider: React.FC<NostrProviderProps> = (props) => {
  const { children } = props;
  const { config } = useAppContext();
  const { logins } = useNostrLogin();

  const queryClient = useQueryClient();

  // Use refs so the pool callbacks always see the latest data without
  // recreating the pool. The refs are written from effects (never during
  // render) to satisfy React's purity rules.
  const relayMetadataRef = useRef(config.relayMetadata);
  const useAppRelaysRef = useRef(config.useAppRelays);

  // Stable ref to the current user's signer for NIP-42 AUTH.
  // The `open()` callback reads from this ref when a relay sends an AUTH
  // challenge, so it always uses the latest signer without recreating the pool.
  const signerRef = useRef<NostrSigner | undefined>(undefined);

  // Lazily create the pool once, via useState's initializer. This keeps
  // the render body pure (no ref writes) while guaranteeing a single
  // NPool instance per provider. The initializer runs exactly once at
  // mount, so reading refs inside its closures is safe.
  // eslint-disable-next-line react-hooks/refs
  const [pool] = useState<NPool>(() => new NPool({
    open(url: string) {
      return new NRelay1(url, {
        // NIP-42: Respond to relay AUTH challenges by signing a kind
        // 22242 ephemeral event with the current user's signer.
        auth: async (challenge: string) => {
          const signer = signerRef.current;
          if (!signer) {
            throw new Error('AUTH failed: no signer available (user not logged in)');
          }
          return signer.signEvent({
            kind: 22242,
            content: '',
            tags: [
              ['relay', url],
              ['challenge', challenge],
            ],
            created_at: Math.floor(Date.now() / 1000),
          });
        },
      });
    },
    reqRouter(filters: NostrFilter[]) {
      const routes = new Map<string, NostrFilter[]>();

      // When useAppRelays is on, always route through the curated app relays
      const source = useAppRelaysRef.current
        ? APP_RELAYS
        : relayMetadataRef.current;

      const readRelays = source.relays
        .filter(r => r.read)
        .map(r => r.url);

      for (const url of readRelays) {
        routes.set(url, filters);
      }

      return routes;
    },
    eventRouter(_event: NostrEvent) {
      const source = useAppRelaysRef.current
        ? APP_RELAYS
        : relayMetadataRef.current;

      const writeRelays = source.relays
        .filter(r => r.write)
        .map(r => r.url);

      return [...new Set<string>(writeRelays)];
    },
    // After the first relay EOSEs, give stragglers 3s to deliver before
    // returning. `0` (wait for every relay) made every query burn its full
    // outer timeout whenever one relay hung, and the empty results blamed on
    // timeouts in the past were actually caused by oversized authors filters
    // (relays never EOSE those) — that root cause is fixed by chunking author
    // lists to ≤100 per REQ. The per-query AbortSignal.timeout guards remain
    // as the backstop for relays that never EOSE at all.
    eoseTimeout: 3000,
  }));

  // Derive the current signer from the active login. This mirrors the
  // logic in useCurrentUser but avoids a circular dependency (useCurrentUser
  // depends on NostrContext which we are providing here).
  const currentLogin = logins[0];
  const currentSigner = useMemo(() => {
    if (!currentLogin) return undefined;
    try {
      switch (currentLogin.type) {
        case 'nsec':
          return NUser.fromNsecLogin(currentLogin).signer;
        case 'bunker':
          return NUser.fromBunkerLogin(currentLogin, pool).signer;
        case 'extension':
          return NUser.fromExtensionLogin(currentLogin).signer;
        default:
          return undefined;
      }
    } catch {
      return undefined;
    }
  }, [currentLogin, pool]);

  // Keep the ref in sync so the AUTH callback always sees the latest signer.
  // Writing refs from an effect (not during render) satisfies purity rules.
  useEffect(() => {
    signerRef.current = currentSigner;
  }, [currentSigner]);

  // Track the last relay fingerprint so we only invalidate when URLs/permissions
  // actually change — not on every config object reference update. This prevents
  // a cascade of invalidations from aborting in-flight feed/article queries
  // before they have time to return results (the root cause of empty Following
  // tabs despite having hundreds of follows loaded).
  //
  // Seed with the initial fingerprint so the mount-time effect doesn't
  // immediately invalidate queries that just started loading.
  const initialFp = relayFingerprint(
    (config.useAppRelays ? APP_RELAYS : config.relayMetadata).relays,
  );
  const prevFingerprintRef = useRef<string>(initialFp);

  const invalidateIfRelaysChanged = useCallback(() => {
    const source = useAppRelaysRef.current ? APP_RELAYS : relayMetadataRef.current;
    const fp = relayFingerprint(source.relays);
    if (fp !== prevFingerprintRef.current) {
      prevFingerprintRef.current = fp;
      queryClient.invalidateQueries();
    }
  }, [queryClient]);

  // Keep refs in sync so pool callbacks always see latest values.
  useEffect(() => {
    relayMetadataRef.current = config.relayMetadata;
    invalidateIfRelaysChanged();
  }, [config.relayMetadata, invalidateIfRelaysChanged]);

  useEffect(() => {
    useAppRelaysRef.current = config.useAppRelays;
    invalidateIfRelaysChanged();
  }, [config.useAppRelays, invalidateIfRelaysChanged]);

  const contextValue = useMemo(() => ({ nostr: pool }), [pool]);

  return (
    <NostrContext.Provider value={contextValue}>
      {children}
    </NostrContext.Provider>
  );
};

export default NostrProvider;
